import { UnparsedView } from '@paragon/contract-library';

import { displayNames, PropertyKey } from './propertyMetadata';
import { JobFilter, SingleOperator } from './store/viewsSlice';

export interface SavedView {
  readonly id: number;
  readonly userEmail: string;
  readonly name: string;
  readonly jobFilters: readonly JobFilter[];
  readonly visibleColumns: readonly PropertyKey[];
  readonly isDefault: boolean;
}

export function parseView(unparsedView: UnparsedView): SavedView {
  return {
    id: unparsedView.id,
    userEmail: unparsedView.userEmail,
    name: unparsedView.name,
    jobFilters: parseJobFilters(unparsedView.filter),
    visibleColumns: parseVisibleColumns(unparsedView.columns),
    isDefault: unparsedView.isDefault,
  };
}

function parseJobFilters(jobFiltersString: string): JobFilter[] {
  if (jobFiltersString.trim() === '') return [];
  return jobFiltersString
    .split(';')
    .map((jobFilterString) => parseJobFilter(jobFilterString.trim())!)
    .filter((jobFilter) => !!jobFilter);
}

function parseJobFilter(jobFilterString: string) {
  if (jobFilterString.trim() === '') return null;
  const conditionals = jobFilterString
    .split(',')
    .map((conditionalString) => parseConditional(conditionalString.trim()))
    .filter((conditional) => conditional != null);
  if (conditionals.some((conditional) => !conditional) || conditionals.length > 3 || conditionals.length === 0) {
    console.error('Unable to parse job filter');
    return null;
  }
  if (conditionals.length === 1) return conditionals[0]!;
  const [firstConditional, secondConditional] = conditionals;
  if (
    firstConditional!.operator !== '>=' ||
    secondConditional!.operator !== '<=' ||
    firstConditional!.propertyKey !== secondConditional!.propertyKey
  ) {
    return null;
  }
  return {
    propertyKey: firstConditional!.propertyKey,
    operator: '>= <=' as const,
    low: firstConditional!.value,
    high: secondConditional!.value,
  };
}

function parseConditional(conditionalString: string) {
  const operator = parseOperator(conditionalString);
  if (operator == null) return null;
  const conditionalParts = conditionalString.split(operator).map((conditionalPart) => conditionalPart.trim());
  if (conditionalParts.length !== 2) return null;
  const propertyKey = parsePropertyKey(conditionalParts[0]);
  if (propertyKey == null) return null;
  return {
    propertyKey,
    operator,
    value: conditionalParts[1],
  };
}

function parseOperator(conditionalString: string): SingleOperator | null {
  return conditionalString.includes('<=')
    ? '<='
    : conditionalString.includes('>=')
      ? '>='
      : conditionalString.includes('<')
        ? '<'
        : conditionalString.includes('>')
          ? '>'
          : conditionalString.includes('!=')
            ? '!='
            : conditionalString.includes('=')
              ? '='
              : null;
}

function parsePropertyKey(displayName: string) {
  const matchingPropertyMetadata = Object.entries(displayNames).find(
    ([_, displayNameInObject]) => displayNameInObject === displayName,
  );
  if (!matchingPropertyMetadata) return null;
  return matchingPropertyMetadata[0] as PropertyKey;
}

function parseVisibleColumns(columns: string): readonly PropertyKey[] {
  return columns.split(',') as readonly PropertyKey[];
}

export function stringifyJobFilters(jobFilter: readonly JobFilter[]) {
  return jobFilter
    .map((jobFilter) => stringifyJobFilter(jobFilter))
    .filter((jobFilterString) => jobFilterString != null)
    .join('; ');
}

function stringifyJobFilter(jobFilter: JobFilter) {
  const displayName = stringifyPropertyKey(jobFilter.propertyKey);
  if (!displayName) return null;
  if (jobFilter.operator === '>= <=') {
    return `${displayName} >= ${jobFilter.low}, ${displayName} <= ${jobFilter.high}`;
  }
  return `${displayName} ${jobFilter.operator} ${jobFilter.value}`;
}

function stringifyPropertyKey(propertyKey: PropertyKey) {
  return displayNames[propertyKey];
}

export function stringifyVisibleColumns(visibleColumns: readonly string[]) {
  return visibleColumns.join(',');
}
